<template>
  <div>
    <grid-view-ag-grid
      v-if="isAgGridProductListEnabled && showGrid"
      ref="agGridComponent"
      :level-entry-key="levelEntryKey"
      :is-unit-manager-view="isUnitManagerView"
      :tension-chart-options="tensionChartOptions"
      @downloadData="downloadDataHandler($event)"
      @toggleFilteringByArchitectureGroup="onToggleFilteringByArchitectureGroup"
    />
    <grid-view
      v-if="!isAgGridProductListEnabled"
      ref="gridViewComponent"
      :headers="headers"
      :items="filteredItems"
      :level-entry-key="levelEntryKey"
      :is-unit-manager-view="isUnitManagerView"
      :architecture-group-description-to-color-map="architectureGroupDescriptionToColorMap"
      :architecture-sub-group-description-to-color-map="architectureSubGroupDescriptionToColorMap"
      :tension-chart-options="tensionChartOptions"
      @downloadData="downloadDataHandler($event)"
      @toggleFilteringByArchitectureGroup="onToggleFilteringByArchitectureGroup"
    />
  </div>
</template>

<script>
import { flatMap, get, keys, omit, pick, isEmpty, map, uniqBy, groupBy } from 'lodash';
import { mapActions, mapGetters, mapState, mapMutations } from 'vuex';
import DataTypes from '@enums/data-types';
import {
  useZones,
  displayMinMaxPenalty,
  includeNormWeight,
  useForecastVolume,
  marginComparisonType,
} from '@enums/feature-flags';
import timeFlexibleEndingsMap from '@enums/time-flexible-endings';
import featureFlagsMixin from '../../../mixins/featureFlags';
import getPriceFieldMixin from '../../../mixins/getPriceField';
import { getMultiLevelSortProductGroupsFunc } from '../../../utils/sort-architecture-groups-util';
import numberFormats from '@enums/number-formats';

export default {
  mixins: [featureFlagsMixin, getPriceFieldMixin],
  props: {
    levelEntryKey: String,
    isUnitManagerView: Boolean,
  },
  data() {
    return {
      showGrid: true,
      articleHeaders: [
        {
          text: '',
          align: 'left',
          sortable: false,
          valuePath: 'architectureGroupDescription',
          class: 'architecture-group-cell-header',
        },
        {
          text: '',
          align: 'left',
          sortable: false,
          valuePath: 'subGroupDescription',
          class: 'architecture-group-cell-header',
        },
        {
          text: 'pricing.productKey',
          align: 'left',
          sortable: true,
          // Label states this is the product key but since we are displaying it
          // we actually use the display key here.
          valuePath: 'productKeyDisplay',
          datatype: DataTypes.str,
          class: 'fixed-width-number',
        },
        {
          text: 'pricing.toolStoreGroup',
          align: 'left',
          sortable: true,
          valuePath: 'toolStoreGroupDescription',
          datatype: DataTypes.str,
          featureFlag: useZones,
          style: 'width:14rem;',
        },
        {
          text: 'pricing.productDescription',
          align: 'left',
          sortable: true,
          valuePath: 'productDescription',
          datatype: DataTypes.str,
          style: 'min-width: 20rem;',
        },
        {
          text: 'pricing.productSizeType',
          align: 'left',
          sortable: true,
          valuePath: 'productSizeType',
          class: 'border-right fixed-width-number',
          datatype: DataTypes.str,
          style: 'min-width: 7rem;width: 7rem;',
        },
      ],
      salesHeaders: [
        {
          text: 'pricing.sales',
          align: 'left',
          sortable: true,
          valuePath: 'yearlySales',
          class: 'fixed-width-number',
          datatype: DataTypes.number,
        },
        {
          text: 'pricing.volume',
          align: 'left',
          sortable: true,
          valuePath: 'yearlyVolume',
          class: 'fixed-width-number',
          datatype: DataTypes.number,
        },
        {
          text: 'pricing.promo',
          align: 'left',
          sortable: true,
          valuePath: 'mandatoryEngineInputs.promoParticipationSales',
          class: 'border-right fixed-width-number',
          datatype: DataTypes.number,
        },
      ],
      staticSenarioHeaders: [
        {
          text: 'pricing.scenarioMargin',
          align: 'left',
          sortable: true,
          valuePath: 'scenarioMargin',
          datatype: DataTypes.number,
          class: 'scenario-colour fixed-width-number',
        },
        {
          text: 'pricing.scenarioSalesImpact',
          align: 'left',
          sortable: true,
          valuePath: 'scenarioSalesImpact',
          class: 'scenario-colour fixed-width-number',
          datatype: DataTypes.number,
        },
        {
          text: 'pricing.scenarioCostImpact',
          align: 'left',
          sortable: true,
          valuePath: 'scenarioCostImpact',
          class: 'border-right scenario-colour fixed-width-number',
          datatype: DataTypes.number,
        },
      ],
      tensionChartOptions: {
        chart: {
          width: 60,
          height: 23,
          spacing: [5, 0, 5, 0],
          style: {
            margin: '0 auto',
          },
        },
        xAxis: {
          offset: -15,
        },
      },
    };
  },
  computed: {
    ...mapState('gridView', [
      'selectedCompetitors',
      'activeTableFilters',
      'alertFilter',
      'selectedHistoricalPeriod',
      'showRegularImpact',
    ]),
    ...mapState('filters', ['selectedPriceType']),
    ...mapState('clientConfig', ['exportConfigs', 'alertTypes', 'toggleLogic']),
    ...mapGetters('context', ['isOWUser']),
    ...mapGetters('gridView', [
      'items',
      'readFromDatabricks',
      'isAgGridProductListEnabled',
      'filteredItems',
    ]),

    architectureGroupDescriptionToColorMap() {
      if (this.isUnitManagerView || isEmpty(this.filteredItems)) {
        return {};
      }
      const architectureGroupDescriptionToPriorityMap = {};
      this.filteredItems.forEach(item => {
        if (architectureGroupDescriptionToPriorityMap[item.architectureGroupDescription]) {
          return;
        }
        architectureGroupDescriptionToPriorityMap[item.architectureGroupDescription] =
          item.architectureGroupPriority;
      });
      const architectureGroups = map(architectureGroupDescriptionToPriorityMap, (value, key) => ({
        architectureGroupDescription: key,
        architectureGroupPriority: value,
      }));
      const sortFunc = getMultiLevelSortProductGroupsFunc('architectureGroup');
      const sortedArchitectureGroups = architectureGroups.sort(sortFunc);
      return sortedArchitectureGroups.reduce((result, architectureGroup, index) => {
        // possible color numbers: 1, 2
        result[architectureGroup.architectureGroupDescription] = (index % 2) + 1;
        return result;
      }, {});
    },

    architectureSubGroupDescriptionToColorMap() {
      if (this.isUnitManagerView || isEmpty(this.filteredItems)) {
        return {};
      }
      const itemsGroupedByAG = groupBy(this.filteredItems, 'architectureGroupDescription');
      const sortFunc = getMultiLevelSortProductGroupsFunc('architectureSubGroup');
      return keys(itemsGroupedByAG).reduce((result, key) => {
        // possible color numbers: 1, 2
        result[key] = uniqBy(itemsGroupedByAG[key].sort(sortFunc), 'subGroupDescription').reduce(
          (res, item, index) => {
            res[item.subGroupDescription] = (index % 2) + 1;
            return res;
          },
          {}
        );
        return result;
      }, {});
    },

    filteredArticleHeaders() {
      return this.filterArrayByFeatureFlag(this.articleHeaders);
    },

    headers() {
      // only show selected competitors in table
      const competitorHeaders = flatMap(this.selectedCompetitors, (c, i, arr) => [
        {
          text: c.competitorDisplayDescription,
          align: 'left',
          sortable: true,
          key: `${
            this.readFromDatabricks
              ? `${this.getPriceField('competitorPrice')}-${c.competitorKey}`
              : `competitor${i + 1}.${this.getPriceField('competitorPrice')}`
          }`,
          valuePath: `${
            this.readFromDatabricks
              ? `competitors.${c.competitorKey}.${this.getPriceField('competitorPrice')}`
              : `competitor${i + 1}.${this.getPriceField('competitorPrice')}`
          }`,
          // index of competitor header: used to keep header text up-to-date with selected competitors
          competitorHeader: i,
          class: 'fixed-width-number',
          datatype: DataTypes.number,
        },
        {
          text: 'pricing.competitorIndex',
          align: 'left',
          sortable: true,
          key: `${
            this.readFromDatabricks
              ? `competitorIndex-${c.competitorKey}`
              : `competitor${i + 1}.competitorIndex`
          }`,
          valuePath: `${
            this.readFromDatabricks
              ? `competitors.${c.competitorKey}.competitorIndex`
              : `competitor${i + 1}.competitorIndex`
          }`,
          class: [i === arr.length - 1 ? 'border-right' : '', 'fixed-width-number'],
          datatype: DataTypes.number,
        },
      ]);

      const costHeaders = [
        {
          text: 'pricing.goLiveCost',
          align: 'left',
          sortable: true,
          valuePath: `${this.getPriceField('nonPromoNetCost')}`,
          class: 'medium-price-field',
          datatype: DataTypes.number,
        },
        {
          text: 'pricing.costDelta',
          align: 'left',
          sortable: true,
          valuePath: `${this.getPriceField('costDelta')}`,
          class: 'border-right medium-price-field',
          datatype: DataTypes.number,
        },
      ];
      const tensionLabel = this.isFeatureFlagEnabled(useZones)
        ? 'pricing.tensionUseZones'
        : 'pricing.tension';
      const tensionHeader = {
        text: tensionLabel,
        align: 'left',
        sortable: false,
        valuePath: 'scenario.tension',
        class: 'fixed-width-number',
      };

      const alertsHeader = {
        text: '',
        sortable: false,
        class: 'alert-padding',
      };

      const priceHeaders = [
        {
          text: 'pricing.weekPriceChange',
          align: 'left',
          sortable: false,
          valuePath: 'priceHistoryWeek',
          class: 'fixed-width-number',
          style: 'width: 6rem',
        },
        {
          text: 'pricing.livePrice',
          align: 'left',
          sortable: true,
          valuePath: `${
            this.readFromDatabricks
              ? this.getPriceFieldObjectPath('livePrice')
              : this.getPriceField('livePrice')
          }`,
          class: 'fixed-width-number',
          datatype: DataTypes.number,
        },
        {
          text: 'pricing.intentionPrice',
          align: 'left',
          sortable: true,
          valuePath: `${this.getPriceFieldObjectPath('intentionPrice')}`,
          class: 'fixed-width-number',
          datatype: DataTypes.number,
        },
        ...(this.toggleLogic[marginComparisonType] === 'live'
          ? [
              {
                text: 'pricing.liveMargin',
                align: 'left',
                sortable: true,
                valuePath: 'liveMargin',
                class: 'fixed-width-number',
                datatype: DataTypes.number,
              },
            ]
          : [
              {
                text: 'pricing.intentionMargin',
                align: 'left',
                sortable: true,
                valuePath: 'intentionMargin',
                class: 'fixed-width-number',
                datatype: DataTypes.number,
              },
            ]),
      ];
      const scenarioPriceHeaders = [
        {
          text: 'pricing.scenarioPrice',
          align: 'left',
          sortable: true,
          valuePath: `${this.getPriceField('scenarioPrice')}`,
          datatype: DataTypes.number,
          class: 'scenario-colour border-left-primary wide-price-field',
        },
        {
          text: 'pricing.scenarioPriceChange',
          align: 'left',
          sortable: true,
          valuePath: `${this.getPriceField('scenarioPriceChange')}`,
          datatype: DataTypes.number,
          class: 'scenario-colour fixed-width-number',
        },
        {
          text: 'pricing.scenarioPriceChangeRatio',
          align: 'left',
          sortable: true,
          valuePath: `${this.getPriceField('scenarioPriceChangeRatio')}`,
          datatype: DataTypes.number,
          class: 'scenario-colour fixed-width-number',
        },
      ];
      return [
        ...this.filteredArticleHeaders,
        ...this.salesHeaders,
        ...costHeaders,
        ...priceHeaders,
        ...scenarioPriceHeaders,
        ...this.staticSenarioHeaders,
        ...competitorHeaders,
        tensionHeader,
        alertsHeader,
      ];
    },
  },

  mounted() {
    this.$root.$on('pre-toggle-expansion', this.onPreToggleExpansion);
  },

  beforeDestroy() {
    this.resetGrid();
    // Unsubscribe to prevent memory leaks
    if (this.$root && this.$root.$off) {
      this.$root.$off('pre-toggle-expansion', this.onPreToggleExpansion);
    }
  },

  methods: {
    ...mapActions('gridView', ['downloadItems']),
    ...mapMutations('gridView', ['setItems']),

    downloadDataHandler({ selectedCompetitorKeys = [] }) {
      const alertTranslationMap = Object.values(this.alertTypes).reduce((acc, alertType) => {
        return {
          ...acc,
          [alertType]: this.$t(`alerts.${alertType}`),
        };
      }, {});

      const timeFlexibleAlerts = keys(timeFlexibleEndingsMap).reduce((acc, ending) => {
        const alertText = this.$tc(`alerts.missingHistoricalData`);
        acc[`missingHistoricalData${ending}`] = alertText;
        return acc;
      }, {});

      const toolStoreGroupTranslation = this.isFeatureFlagEnabled(useZones)
        ? {
            toolStoreGroupDescription: this.$t('pricing.toolStoreGroup'),
          }
        : {};

      const storeGroupReferencePriceTranslation = this.isFeatureFlagEnabled(useZones)
        ? {
            storeGroupReferencePrice: this.$t('pricing.storeGroupReferencePrice'),
          }
        : {};

      const minMaxReferencePriceTranslation = this.isFeatureFlagEnabled(displayMinMaxPenalty)
        ? {
            minMaxReferencePrice: this.$t('pricing.minMaxReferencePrice'),
          }
        : {};

      const livePriceTranslation = `${this.$t('pricing.livePrice')} ${this.$t(
        'pricing.mainHeaders.prices'
      )}`;

      const normWeightTranslation = this.toggleLogic[includeNormWeight]
        ? {
            normWeight: this.$t('pricing.normWeight'),
            scenarioPricePerNormWeight: this.$t('pricing.scenarioPricePerNormWeight'),
            scenarioPricePerContentUnit: this.$t('pricing.scenarioPricePerContentUnit'),
          }
        : {};

      const marginTranslation =
        this.toggleLogic[marginComparisonType] === 'live'
          ? { liveMargin: this.$t('pricing.exportLiveMargin') }
          : { intentionMargin: this.$t('pricing.exportIntentionMargin') };

      const translationMap = {
        productKeyDisplay: this.$t('pricing.productKey'),
        ...toolStoreGroupTranslation,
        productDescription: this.$t('pricing.productDescription'),
        productSizeType: this.$t('pricing.productSizeType'),
        // yearlySales and yearlyVolume will be overwritten by seleted time-flexible field
        yearlySales: this.$t(
          `pricing.${
            this.isFeatureFlagEnabled(useForecastVolume) ? 'exportHistoricSales' : 'exportSales'
          }`
        ),
        ...(this.isFeatureFlagEnabled(useForecastVolume)
          ? { forecastSales: this.$t('pricing.exportForecastSales') }
          : {}),
        yearlyVolume: this.$t(
          `pricing.${
            this.isFeatureFlagEnabled(useForecastVolume) ? 'exportHistoricVolume' : 'exportVolume'
          }`
        ),
        ...(this.isFeatureFlagEnabled(useForecastVolume)
          ? { forecastVolume: this.$t('pricing.exportForecastVolume') }
          : {}),
        promo: this.$t('pricing.promo'),
        nonPromoNetCost: this.$t('pricing.goLiveCost'),
        costDelta: this.$t('pricing.costDelta'),
        ...(this.readFromDatabricks
          ? { 'livePrice.price': livePriceTranslation }
          : { livePrice: livePriceTranslation }),
        'intentionPrice.price': `${this.$t('pricing.intentionPrice')} ${this.$t(
          'pricing.mainHeaders.prices'
        )}`,
        ...marginTranslation,
        scenarioPrice: this.$t('pricing.exportScenarioPrice'),
        scenarioPriceChange: this.$t('pricing.exportScenarioPriceChange'),
        scenarioPriceChangeRatio: this.$t('pricing.exportScenarioPriceChangeRatio'),
        scenarioMargin: this.$t('pricing.exportScenarioMargin'),
        scenarioSalesImpact: this.$t('pricing.exportTotalSalesImpact'),
        regularSalesImpact: this.$t('pricing.exportRegularSalesImpact'),
        scenarioCostImpact: this.$t('pricing.exportTotalCostImpact'),
        regularCostImpact: this.$t('pricing.exportRegularCostImpact'),
        economicReferencePrice: this.$t('pricing.economicReferencePrice'),
        architectureReferencePrice: this.$t('pricing.architectureReferencePrice'),
        competitorReferencePrice: this.$t('pricing.competitorReferencePrice'),
        ...storeGroupReferencePriceTranslation,
        ...minMaxReferencePriceTranslation,
        ...normWeightTranslation,
        ...omit(alertTranslationMap, 'missingHistoricalData'),
        ...pick(timeFlexibleAlerts, [`missingHistoricalData${this.selectedHistoricalPeriod}`]),
        competitorIndex: this.$t('pricing.competitorIndex'),
      };

      this.downloadItems({
        pricingGroupId: this.pricingGroupId,
        translationMap,
        isUnitManagerView: this.isUnitManagerView,
        selectedCompetitorKeys,
        columnFormatters: {
          [this.$t('pricing.productKey')]: {
            type: get(
              this.exportConfigs,
              'exportToExcel.columnFormatter.productKeyDisplay',
              numberFormats.integer
            ),
          },
        },
      });
    },
    onToggleFilteringByArchitectureGroup(eventData) {
      this.$emit('toggleFilteringByArchitectureGroup', eventData);
    },

    downloadCurrentView() {
      // For ag-grid: use local export functionality
      // For legacy grid: use server-side download
      if (this.isAgGridProductListEnabled) {
        // Use ref to access the ag-grid component and call its local download method
        const agGridComponent = this.$refs.agGridComponent;
        if (agGridComponent && agGridComponent.downloadCurrentView) {
          agGridComponent.downloadCurrentView();
        }
      } else {
        // Legacy grid: call the download method which handles competitor selection modal
        const gridViewComponent = this.$refs.gridViewComponent;
        if (gridViewComponent && gridViewComponent.download) {
          gridViewComponent.download();
        }
      }
    },

    onPreToggleExpansion() {
      this.resetGrid();
    },
    resetGrid() {
      this.showGrid = false;
      this.setItems({ items: [] });
      // Force computed property to update and break reference to old array
      // eslint-disable-next-line no-unused-expressions
      this.filteredItems;
    },
  },
};
</script>

<style lang="scss">
.wide-price-field {
  width: 10rem;
}
.medium-price-field {
  width: 6rem;
}
</style>
